import { ArrowRight, CheckCircle2, Gauge, PlayCircle, Workflow } from "lucide-react";
import type { ReactNode } from "react";

type ActionPanelProps = {
  onOpenLiveAgent: () => void;
};

export default function ActionPanel({ onOpenLiveAgent }: ActionPanelProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
        <button
          type="button"
          onClick={onOpenLiveAgent}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-primary-border bg-primary-dim px-5 py-2.5 text-sm font-semibold text-primary-text transition hover:border-primary hover:bg-bg-hover sm:w-auto"
        >
          Try live agent
          <PlayCircle className="h-4 w-4" />
        </button>
        <a
          href="https://agents.anter.ai"
          target="_blank"
          rel="noreferrer"
          className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-border bg-bg-secondary px-5 py-2.5 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover sm:w-auto"
        >
          Open full builder preview
          <ArrowRight className="h-4 w-4 text-primary transition-transform group-hover:translate-x-1" />
        </a>
      </div>
      <div
        id="ask-anter"
        className="inline-flex w-full items-center justify-center rounded-full border border-border bg-bg-secondary px-3 py-1 text-center text-xs text-text-muted sm:w-fit sm:text-left"
      >
        Use the <span className="ml-1 font-semibold text-primary-text">Ask Anter</span> button for
        live chat
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-1">
        <MetricCard
          icon={<Gauge className="h-4 w-4 text-primary" />}
          label="Latency tracking"
          value="Visible per run"
          hint="Recorded locally. Absolute numbers vary by environment."
        />
        <MetricCard
          icon={<Workflow className="h-4 w-4 text-blue" />}
          label="Tool calls / run"
          value="2.3"
        />
        <MetricCard
          icon={<CheckCircle2 className="h-4 w-4 text-green" />}
          label="Successful handoffs"
          value="100%"
        />
      </div>
      <p className="text-xs leading-relaxed text-text-faint">
        The key signal is traceability, not a single latency number. You can inspect timing and
        routing at each step.
      </p>
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-bg-secondary/85 p-3">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.13em] text-text-faint">
        {icon}
        {label}
      </div>
      <div className="text-xl font-bold text-text">{value}</div>
      {hint ? <div className="mt-1 text-xs text-text-faint">{hint}</div> : null}
    </div>
  );
}
